import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SimpleParticle from "./SimpleParticle";
import { Footer } from "react-day-picker";
import ReadySection from "./ReadySection";

const faqs = [
  {
    q: "How do you save my time?",
    a: "We handle editing, posting, and strategy. You just record.",
  },
  {
    q: "Will you guide my recording setup?",
    a: "Absolutely. We help with lighting, audio, and framing remotely.",
  },
  {
    q: "How fast is your delivery?",
    a: "48–72 hours, depending on the project.",
  },
  {
    q: "Do I need technical knowledge?",
    a: "No. You record. We handle the rest.",
  },
  {
    q: "What if my videos aren’t performing?",
    a: "We optimize pacing, hooks, and SEO to improve results.",
  },
];

// LEFT CONTENT FADE UP STAGGER
const leftStagger = {
  hidden: { opacity: 0, y: 40, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { staggerChildren: 0.15, duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  },
};

const itemFade = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0 },
};

// FAQ CARD ENTRY ANIMATION
const faqCardVariant = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
  },
};

const FaqSection = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggle = (i) => {
    setOpenItems((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );
  };

  return (
    <>
  <section id="faq" className="py-28 px-4 sm:px-6 bg-transparent font-inter pb-[0px] scale-100 md:scale-[0.90] pt-[0px] mb-[20px] mt-[40px] md:pt-[50px]">
      <div className="max-w-[1000px] mx-auto grid grid-cols-1 md:grid-cols-2 m-center gap-12">

        {/* LEFT SIDE ANIMATED TEXT BLOCK */}
        <motion.div
          variants={leftStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="flex flex-col items-center md:items-start text-center md:text-left"
        >
          <motion.div variants={itemFade}>
            <div className="px-4 py-1.5 rounded-full bg-white border border-dashed border-gray-300 inline-flex items-center gap-2 shadow-sm mb-2 md:mb-6 mx-auto md:mx-0 justify-center md:justify-start">
              <div className="w-2 h-2 rounded-full bg-[#3C5DF7]"></div>
              <span className="text-[17px] text-gray-700 font-medium">FAQ</span>
            </div>
          </motion.div>

          <motion.h2
            variants={itemFade}
            className="text-[40px] md:text-[52px] leading-[1.02] font-semibold text-[#171717] mb-4 text-center md:text-left"
          >
            Got 
            <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}> Questions?</span><br />
            <span className="font-semibold"> We Got</span> 
            <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}> Answers</span>
          </motion.h2>

          <motion.p
            variants={itemFade}
            className="text-[17px] text-[#444] max-w-[380px] leading-[1.55] font-medium"
          >
            Straightforward, no–fluff answers to help you feel confident about working with us.
          </motion.p>
        </motion.div>

        {/* RIGHT SIDE FAQ */}
        <div className="flex flex-col gap-4">
          {faqs.map((item, index) => {
            const isOpen = openItems.includes(index);

            return (
              <motion.div
                key={index}
                variants={faqCardVariant}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ scale: 1.015, translateY: -2 }}
                className={`relative ml-[10px] cursor-pointer rounded-[12px] overflow-hidden 
                  transition-all duration-300 border border-dashed 
                  ${
                    isOpen
                      ? "bg-[#171717] border-[#2a2a2a] text-white shadow-xl"
                      : "bg-white border-gray-200 text-black shadow-sm"
                  }
                `}
                onClick={() => toggle(index)}
              >
                {/* GRADIENT SWEEP ON OPEN */}
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, x: "-100%" }}
                    animate={{ opacity: 0.15, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0 bg-gradient-to-r from-[#3C5DF7]/40 to-transparent pointer-events-none"
                  />
                )}

                {/* PARTICLES WHEN OPEN */}
                {isOpen && <SimpleParticle />}

                {/* QUESTION HEADER */}
                <div className="flex justify-between items-center p-6 relative z-10">
                  <p className="text-[16px] font-semibold">{item.q}</p>

                  {/* PLUS → MINUS ICON MORPH */}
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="relative"
                  >
                    <svg width="20" height="20" fill="none">
                      <motion.path
                        d="M6 8l6 6 6-6"
                        stroke={isOpen ? "#fff" : "#000"}
                        strokeWidth="2"
                        strokeLinecap="round"
                        animate={{
                          d: isOpen
                            ? "M6 12h12" // minus
                            : "M6 8l6 6 6-6", // chevron
                        }}
                        transition={{ duration: 0.25 }}
                      />
                    </svg>
                  </motion.span>
                </div>

                {/* ANSWER */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0, y: -10 }}
                      animate={{ opacity: 1, height: "auto", y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -10 }}
                      transition={{ duration: 0.35 }}
                      className="relative z-10"
                    >
                      <p className="px-6 pb-6 text-[15px] text-gray-300 leading-relaxed">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
    <ReadySection />
    </>
  );
};

export default FaqSection;
